import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { apiInstance } from '../../axiosApi';

export const agentListGet = createAsyncThunk('chat-agent', async (params) => {
  return await apiInstance.get('/chat/agent', { params });
});

export const supervisorChatListGet = createAsyncThunk('chat-supervisor', async (params) => {
  return await apiInstance.get('/chat/supervisor', { params });
});

export const chatHistoryGet = createAsyncThunk('chat-history', async (params) => {
  return await apiInstance.get('/chat/chat-history', { params });
});

export const sendChatMsg = createAsyncThunk('chat-send-message', async (formData) => {
  return await apiInstance.post('/chat/send-message', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
});

export const markAsRead = createAsyncThunk('chat-mark-read', async (payload) => {
  return await apiInstance.put('/chat/mark-read', payload);
});

export const getChatCount = createAsyncThunk('chat-unread', async (params) => {
  return await apiInstance.get('/chat/unread', { params });
});

export const getChatReport = createAsyncThunk('chat-report', async (params) => {
  return await apiInstance.get('/chat-report', { params });
});

export const getChatReportHistory = createAsyncThunk('chat-report/chat-history', async (params) => {
  return await apiInstance.get('/chat-report/chat-history', { params });
});

export const getWhatsappReport = createAsyncThunk('whatsapp-report', async (params) => {
  return await apiInstance.get('/whatsapp-report', { params });
});

export const getWhatsappReportHistory = createAsyncThunk(
  'whatsapp-report/chat-history',
  async (params) => {
    return await apiInstance.get('/whatsapp-report/chat-history', { params });
  }
);

const initialState = {
  agentList: [],
  supervisorChatList: [],
  activeChatDetails: null,
  countList: [],
  supervisorMsgList: []
};

const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    setActiveChatDetails(state, action) {
      state.activeChatDetails = action.payload;
    },
    setSupervisorMsgList(state, action) {
      const { id, is_delete } = action.payload;
      if (is_delete) {
        state.supervisorMsgList = state.supervisorMsgList.filter((item) => item !== id);
      } else {
        if (!state.supervisorMsgList.includes(id)) {
          state.supervisorMsgList.push(id);
        }
      }
    },
    clearChatSlice: () => initialState
  },
  extraReducers: (builder) => {
    builder.addCase(agentListGet.fulfilled, (state, action) => {
      const data = [...(action?.payload?.data || [])];
      state.agentList = data?.map((val) => {
        return {
          ...val,
          name: val?.Username,
          id: val?.user_id,
          isActive: Number(val?.type) === 0
        };
      });
    });
    builder.addCase(supervisorChatListGet.fulfilled, (state, action) => {
      const data = [...(action?.payload?.data || [])];
      state.supervisorChatList = data?.map((val) => {
        return {
          ...val,
          name: val?.username,
          id: val?.supervisor_id,
          isActive: val?.session_id ? true : false
        };
      });
    });
    builder.addCase(getChatCount.fulfilled, (state, action) => {
      state.countList = [...(action?.payload?.data || [])];
    });
  }
});

export default chatSlice.reducer;
export const { clearChatSlice, setActiveChatDetails, setSupervisorMsgList } = chatSlice.actions;

export const selectAgentList = (state) => state.chat.agentList;
export const useAgentList = () => {
  const agentList = useSelector(selectAgentList);
  return useMemo(() => agentList, [agentList]);
};

export const selectSupervisorChatList = (state) => state.chat.supervisorChatList;
export const useSupervisorChatList = () => {
  const supervisorChatList = useSelector(selectSupervisorChatList);
  return useMemo(() => supervisorChatList, [supervisorChatList]);
};

export const selectActiveChatDetails = (state) => state.chat.activeChatDetails;
export const useActiveChatDetails = () => {
  const activeChatDetails = useSelector(selectActiveChatDetails);
  return useMemo(() => activeChatDetails, [activeChatDetails]);
};

export const selectCountList = (state) => state.chat.countList;
export const useCountList = () => {
  const countList = useSelector(selectCountList);
  return useMemo(() => countList, [countList]);
};

export const selectSupervisorMsgList = (state) => state.chat.supervisorMsgList;
export const useSupervisorMsgList = () => {
  const supervisorMsgList = useSelector(selectSupervisorMsgList);
  return useMemo(() => supervisorMsgList, [supervisorMsgList]);
};
